import config from "@Config/index";
import axios from "axios";
import getCookie from "helpers/getCookie";
import setCookie from "helpers/setCookie";
import deleteCookie from "helpers/deleteCookie";

import { isClientSideAuthEnabled } from "@Utils/isClientSideAuthEnabled";
import { getSkipApiCache } from "../server/ssrRequestContext";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";

const setupAxios = () => {
  axios.defaults.baseURL = config?.API_URL;
  axios.defaults.timeout = config?.apiParams?.timeout;

  axios.interceptors.request.use(
    (requestConfig) => {
      // for user auth token

      requestConfig.params = requestConfig.params || {};

      // Bypass backend cache when SSR request has cache=false
      if (__SERVER__ && getSkipApiCache()) {
        requestConfig.params.cache = "false";
      }

      // Bypass backend cache on client-side when page was loaded with ?cache=false
      if (__CLIENT__) {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get("cache") === "false") {
          requestConfig.params.cache = "false";
        }
      }

      if (requestConfig?.withCredentials && isClientSideAuthEnabled()) {
        let userAuthToken = getCookie("user_auth_token");
        if (typeof window !== "undefined" && !userAuthToken) {
          userAuthToken = window?.localStorage?.getItem("user_auth_token");
        }
        if (userAuthToken) {
          requestConfig.params.user_auth_token = userAuthToken;
        }
      }

      const isWhiteLabelEnabled = getCookie("is_whitelabel");
      if (config?.IS_WHITELABEL_APP || isWhiteLabelEnabled) {
        requestConfig.params.is_whitelabel = isWhiteLabelEnabled || "true";
      }

      // For user_flow_token
      const userFlowToken = getCookie("user_flow_token");

      if (config?.IS_CHINA_APP) {
        requestConfig.headers["is-china-app"] = "true";
      }

      const isUserFlowRequest = requestConfig?.url?.includes("user_flow/");
      const isWhitelabel = config?.IS_WHITELABEL_APP || isWhiteLabelEnabled;

      if ((userFlowToken && isUserFlowRequest) || isWhitelabel) {
        requestConfig.params.user_flow_token = userFlowToken;
      }

      return requestConfig;
    },
    (error) => {
      console.error("Request interceptor error:", error);
      return Promise.reject(error);
    },
  );

  axios.interceptors.response.use(
    (response) => {
      // for user_flow_token
      const userFlowToken = response?.data?.data?.data?.user_flow_token;

      // for user_auth_token
      const userAuthToken = response.data?.data?.user_auth_token;

      if (userAuthToken) {
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 20);
        setCookie({
          cname: "user_auth_token",
          cvalue: userAuthToken,
          exdays: 7300,
          secure: true,
        });
        setCookie({
          cname: "user_auth_token",
          cvalue: userAuthToken,
          exdays: 7300,
          secure: true,
          domain: `.${config.WEB_SERVICE_HOST}`,
        });
      }

      if (userFlowToken) {
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 20);
        setCookie({
          cname: "user_flow_token",
          cvalue: userFlowToken,
          exdays: 7300,
          secure: true,
          httpOnly: false,
        });
        // Notify native shell immediately so it can store the token without
        // waiting for a cookie sync on the next page-load or dismantle.
        sendWebViewEvent(WEB_VIEW_APP_EVENTS.USER_FLOW_TOKEN, { token: userFlowToken });
      }

      return response;
    },
    (error) => {
      console.error("Response error:", error);
      if (error.response && error.response.status === 401) {
        if (__CLIENT__) {
          deleteCookie("user_auth_token");
          deleteCookie("user_flow_token");
        }
      }
      return Promise.reject(error);
    },
  );
};

setupAxios();

export default axios;
